import ModalFooter from 'react-bootstrap/ModalFooter'
import { Button } from '../../../../components/Button/Button'
import { Modal } from '../../../../components/Modal/Modal'
import { Col, Container, Row } from 'react-bootstrap'
import React, { useCallback } from 'react'
import { CountdownButton } from '../../../../components/Button/CountdownButton'
import { logout } from '../../../../store/auth/authSlice'
import { useAppDispatch } from '../../../../store/hooks'

export const ErrorModal = ({ show, onHide, body, onStopMeeting }: { show: boolean; onHide: () => void; body: React.ReactNode; onStopMeeting: () => void }) => (
  <Modal
    onHide={onHide}
    show={show}
    header={<></>}
    body={body}
    footer={
      <ModalFooter>
        <Button variant='primary' onClick={onStopMeeting}>
          Stop meeting
        </Button>
      </ModalFooter>
    }
    showFooterActions={false}
  />
)

export const SessionExpiredModal = ({ show, onHide, onLogin }: { show: boolean; onHide: () => void; onLogin: () => void }) => {
  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>Authentication Error</h4>
        </Col>
      </Row>
      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>Your session is expired, please login again and you can finish this meeting manually.</div>
        </Col>
      </Row>
    </Container>
  )

  return (
    <Modal
      onHide={onHide}
      show={show}
      header={<></>}
      body={body}
      footer={
        <ModalFooter>
          <Button variant='primary' onClick={onLogin}>
            Login
          </Button>
        </ModalFooter>
      }
      showFooterActions={false}
    />
  )
}

export const BlockerModal = ({ show, onPrimary, onSecondary }: { show: boolean; onPrimary: () => void; onSecondary: () => void }) => {
  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>Are you sure you want to leave the meeting?</h4>
        </Col>
      </Row>
      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>You have an active meeting in progress.</div>
        </Col>
      </Row>
    </Container>
  )
  return (
    <Modal
      onHide={onSecondary}
      show={show}
      header={<></>}
      showFooterActions={true}
      body={body}
      onFooterPrimaryAction={onPrimary}
      footerPrimaryActionTitle='Stop Meeting'
      onFooterSecondaryAction={onSecondary}
      footerPrimaryActionButtonVariant='primary'
    />
  )
}

export const MeetingAlreadyFinishedModal = ({ show, onGoToMeeting }: { show: boolean; onGoToMeeting: () => void }) => {
  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>Meeting Finished</h4>
        </Col>
      </Row>
      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>Your meeting has already finished. Click below to go to the meeting.</div>
        </Col>
      </Row>
    </Container>
  )

  return (
    <Modal
      backdrop='static'
      keyboard={false}
      onHide={onGoToMeeting}
      show={show}
      header={<></>}
      body={body}
      showFooterActions={false}
      footer={
        <ModalFooter>
          <Button variant='primary' onClick={onGoToMeeting}>
            Go To Meeting
          </Button>
        </ModalFooter>
      }
    />
  )
}

export const MuteWarningModal = ({ show, onTimeout }: { show: boolean; onTimeout: () => void }) => {
  const body = (
    <Container className='p-0'>
      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>
            No one is speaking in this meeting. The meeting will automatically end in 5 minutes unless speech is detected. Make sure your microphone is working
            and not muted.
          </div>
        </Col>
      </Row>
      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>This message will dismiss when you start speaking.</div>
        </Col>
      </Row>
    </Container>
  )

  return (
    <Modal
      show={show}
      header={<h4 className='d-flex justify-content-center align-items-center'>No speech detected</h4>}
      body={body}
      footer={
        show && (
          <div className='d-flex gap-2 flex-end'>
            <CountdownButton variant={'danger'} startMinutes={5} onTimeout={onTimeout}>
              End Meeting in
            </CountdownButton>
          </div>
        )
      }
      showFooterActions={false}
    />
  )
}

const FIVE_MIN_IN_SEC = 5 * 60

export const ExpireWarningModal = ({
  show,
  sessionExpirationSeconds,
  onStartAnyWay,
}: {
  show: boolean
  sessionExpirationSeconds: number
  onStartAnyWay: () => void
}) => {
  const dispatch = useAppDispatch()

  const body = (
    <Container className='p-0'>
      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>
            You'll be automatically logged out mid-meeting if you start now. This breaks the transcript and causes problems that often can't be fixed.
          </div>
        </Col>
      </Row>
      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>Log in now to extend your session. You'll return here to start your meeting.</div>
        </Col>
      </Row>
    </Container>
  )

  const onReLogin = useCallback(() => {
    dispatch(logout({ location: window.location.pathname }))
  }, [dispatch])

  const minutesLeft = Math.floor(sessionExpirationSeconds / 60) < 1 ? 1 : Math.floor(sessionExpirationSeconds / 60)

  const prefix = sessionExpirationSeconds > 0 && sessionExpirationSeconds <= FIVE_MIN_IN_SEC ? 'less than ' : ''

  return (
    <Modal
      show={show}
      header={
        <h4 className='d-flex justify-content-center align-items-center'>
          Your login expires in {prefix}
          {minutesLeft} minutes
        </h4>
      }
      body={body}
      footer={
        show && (
          <div className='d-flex gap-2 flex-end'>
            <Button variant={'secondary'} onClick={onStartAnyWay}>
              Start anyway
            </Button>
            <Button variant={'primary'} onClick={onReLogin}>
              Log in now
            </Button>
          </div>
        )
      }
      showFooterActions={false}
    />
  )
}
